import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import {
  Button,
  ButtonsWrapper,
  DeleteModal,
  ICONS,
  Loader,
  PLATFORM_THEME_COLORS,
  Typography,
  TYPOGRAPHY_SIZE,
} from '@thinknet-gmbh/platform-common-ui';

import {
  EmployeeQuestionnaireStatusesEnum,
  PERMISSION_ACTIONS,
  PLATFORM_PERMISSIONS,
} from '@thinknet-gmbh/platform-common';

import { useRouteQuery } from 'src/common/helpers';

import { useGetCurrentMemberAccount } from 'src/hooks/accounts/get-current-member-account.hook';
import { useDeclineEmployeeDocumentTemplate } from 'src/hooks/employee-questionnaire/decline-employee-questionnaire.hook';
import { useGetEmployeeQuestionnaireById } from 'src/hooks/employee-questionnaire/get-employee-questionnaire-by-id.hook';
import { useGetEmployeeDeviceInfoByQuestionnaireId } from 'src/hooks/employee-questionnaire/get-employee-questionnaire-device-info.hook';

import PermissionCheck from 'src/components/permission-check';
import { BooleanSwitchInfo } from 'src/components/employee/components/boolean-switch-info';
import { DrawerComponent } from '../../../../components/custom-drawer';
import ChangeHealthInsurance from '../../../settings/components/health-insurance/components/change-health-insurance/change-health-insurance.component';
import { PersonalInfoQuestionnairesDetails } from './components/personal-info-questionnaires-details';
import { EmergencyContactQuestionnairesDetails } from './components/emergency-contact-questionnaires-details';
import { PaymentAndInsuranceQuestionnairesDetails } from './components/payment-and-insurance-questionnaires-details';
import { EducationsAndSkillsQuestionnairesDetails } from './components/educations-and-skills-questionnaires-details';
import { DeviceInfo } from './components/device-info';

import { ROUTES } from 'src/components/app/app-routes.constants';
import {
  ALLOW_ACTION_QUESTIONNAIRE_STATUSES,
  QUESTIONNAIRE_MODALS,
} from '../open-questionnaires-list/open-questionnaires-list.constants';

import { EMPLOYEE_PAGES } from '../../employee.constants';

import {
  HeaderWrapper,
  InfoBlock,
  InfoElement,
  InfoWrapper,
  OpenQuestionnairesDetailsWrapper,
  InfoWrapperTitle,
} from './open-questionnaires-details.styles';



const OpenQuestionnairesDetails = () => {
  const { t } = useTranslation(['settings', 'employee']);
  const query = useRouteQuery();
  const navigate = useNavigate();

  const _id: string = query.get('id') || '';

  const [activeModal, setActiveModal] = useState<null | string>(null);

  const { member } = useGetCurrentMemberAccount();

  const { employeeQuestionnaire, loading } = useGetEmployeeQuestionnaireById(_id as string);
  const { deviceInfo } = useGetEmployeeDeviceInfoByQuestionnaireId(_id as string);

  const {
    responsiblePersonsIds,
    employeePersonalInfo,
    emergencyContact,
    educationAndSkills,
    status,
    email,
    paymentAndInsurance,
    questionnaireSettings,
    finishInformation,
    signature,
    healthInsurance,
    personalIdCardFront,
    personalIdCardBack,
    healthInsuranceCard,
  } = employeeQuestionnaire || {};

  const declineEmployeeQuestionnaire = useDeclineEmployeeDocumentTemplate({
    onSuccess: () => {
      navigate(`${ROUTES.employee}?type=${EMPLOYEE_PAGES.openQuestionnaires}`);
    },
  });

  const closeModal = () => {
    setActiveModal(null);
  };

  const onDeleteSubmit = () => {
    declineEmployeeQuestionnaire({
      variables: {
        payload: {
          openQuestionnaireId: _id,
          newStatus: EmployeeQuestionnaireStatusesEnum.declined,
          currentMemberId: member?._id,
        },
      },
    });
    closeModal();
  };

  const isHasAccessToChange =
    responsiblePersonsIds?.includes(member?._id) &&
    ALLOW_ACTION_QUESTIONNAIRE_STATUSES.includes(status);

  const isHasAccessToCreate = EmployeeQuestionnaireStatusesEnum.received === status;

  const handleCreate = () => {
    navigate(`${ROUTES.employee}/${EMPLOYEE_PAGES.acceptQuestionnaire}?id=${_id}`);
  };

  const handleOpenModal = (type: string) => () => {
    setActiveModal(type);
  };

  const senderName = `${employeePersonalInfo?.firstName || ''} ${employeePersonalInfo?.lastName ||
    ''}`;

  const employeePersonalInfoData = {
    email,
    ...employeePersonalInfo,
  };

  if (loading || !_id) {
    return <Loader />;
  }

  return (
    <OpenQuestionnairesDetailsWrapper>
      <DrawerComponent
        title={t('settings:healthInsurance.labels.addNewHealthInsurance')}
        subtitle={t('settings:healthInsurance.labels.addNewHealthInsuranceSubtitle')}
        onClose={closeModal}
        visible={activeModal === QUESTIONNAIRE_MODALS.createHealthInsurance}
        destroyOnClose
      >
        <ChangeHealthInsurance
          healthInsuranceCardName={paymentAndInsurance?.healthInsuranceCardName}
          onClose={closeModal}
        />
      </DrawerComponent>
      <DeleteModal
        translate={t}
        isOpened={activeModal === QUESTIONNAIRE_MODALS.delete}
        onClose={closeModal}
        submitBtnText={t('common:buttons.delete')}
        title={t('employee:questionnaire.labels.deleteModal.title')}
        subTitle={t('employee:questionnaire.labels.deleteModal.subtitle')}
        onSubmit={onDeleteSubmit}
      />
      <HeaderWrapper>
        <Typography weight="bold" size={TYPOGRAPHY_SIZE.medium}>
          {`${questionnaireSettings?.name}-${senderName}`}
        </Typography>
        {isHasAccessToChange && (
          <ButtonsWrapper>
            <PermissionCheck
              permission={PLATFORM_PERMISSIONS.memberManagement}
              action={PERMISSION_ACTIONS.update}
              createdBy={member?.account?._id}
            >
              <Button
                danger={true}
                type="primary"
                ghost
                color={PLATFORM_THEME_COLORS.tone.red}
                icon={ICONS.xCloseIcon}
                iconColor={PLATFORM_THEME_COLORS.tone.red}
                onClick={handleOpenModal(QUESTIONNAIRE_MODALS.delete)}
              >
                {t('common:buttons.decline')}
              </Button>
            </PermissionCheck>
            {isHasAccessToCreate && (
              <PermissionCheck
                permission={PLATFORM_PERMISSIONS.memberManagement}
                action={PERMISSION_ACTIONS.create}
                createdBy={member?.account?._id}
              >
                <Button
                  type="primary"
                  icon={ICONS.plus}
                  iconColor={PLATFORM_THEME_COLORS.primary.white}
                  onClick={handleCreate}
                >
                  {t('employee:buttons.createNewEmployee')}
                </Button>
              </PermissionCheck>
            )}
          </ButtonsWrapper>
        )}
      </HeaderWrapper>
      {employeePersonalInfoData && (
        <PersonalInfoQuestionnairesDetails
          employeePersonalInfoData={employeePersonalInfoData}
          personalIdCardBack={personalIdCardBack}
          personalIdCardFront={personalIdCardFront}
        />
      )}

      {paymentAndInsurance && (
        <PaymentAndInsuranceQuestionnairesDetails
          paymentAndInsurance={paymentAndInsurance}
          healthInsuranceCard={healthInsuranceCard}
          healthInsurance={healthInsurance}
          handleOpenModal={handleOpenModal(QUESTIONNAIRE_MODALS.createHealthInsurance)}
        />
      )}

      {emergencyContact && (
        <EmergencyContactQuestionnairesDetails emergencyContact={emergencyContact} />
      )}

      {educationAndSkills && (
        <EducationsAndSkillsQuestionnairesDetails educationAndSkills={educationAndSkills} />
      )}
      {finishInformation && (
        <InfoWrapper>
          <InfoWrapperTitle>
            <Typography weight="bold" size={TYPOGRAPHY_SIZE.medium}>
              {t('employee:questionnaire:labels.finishInformation')}
            </Typography>
          </InfoWrapperTitle>

          <InfoBlock>
            <InfoElement>
              <Typography weight="bold">
                {t('employee:questionnaire.finishInformation.accuracyConfirmation')}
              </Typography>
              <BooleanSwitchInfo t={t} defaultChecked={!!finishInformation?.accuracyConfirmation} />
            </InfoElement>
            <InfoElement>
              <Typography weight="bold">
                {t('employee:questionnaire.finishInformation.privacyPolicyConfirmation')}
              </Typography>
              <BooleanSwitchInfo
                t={t}
                defaultChecked={!!finishInformation?.privacyPolicyConfirmation}
              />
            </InfoElement>
          </InfoBlock>
        </InfoWrapper>
      )}
      {deviceInfo && <DeviceInfo deviceInfo={deviceInfo} />}
      {signature?.location && (
        <InfoWrapper>
          <InfoWrapperTitle>
            <Typography weight="bold" size={TYPOGRAPHY_SIZE.medium}>
              {t('employee:questionnaire.finishInformation.signature')}
            </Typography>
          </InfoWrapperTitle>
          <InfoBlock>
            <img src={signature.location} alt={'signature'} />
          </InfoBlock>
        </InfoWrapper>
      )}
    </OpenQuestionnairesDetailsWrapper>
  );
};

export default OpenQuestionnairesDetails;
